import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  fetchMeetingConfig,
  fetchMeetingTemplates,
  createMeetingSession,
  uploadMeetingAudio,
  fetchMeetingSession,
  listMeetingSessions,
  downloadMeetingArtifact,
  renameMeetingSpeakers,
  uploadTextSession,
} from '../services/meetings';
import { createPcmStreamer } from '../utils/pcmWorklet';
import { loadSettings, persistSettings } from '../utils/settings';
import {
  startLocalSession,
  saveAudioChunk,
  finishLocalSession,
  recoverRecordingBlob,
  findInterruptedSessions,
} from '../utils/audioStore';
import { useAuth } from '../contexts/AuthContext';

const styles = {
  page: {
    padding: '24px',
    background: '#f3f4f6',
    minHeight: '100vh',
  },
  section: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.08)',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#111827',
  },
  subtitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#1f2937',
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
  },
  templateCard: (active) => ({
    border: active ? '2px solid #2563eb' : '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '16px',
    cursor: 'pointer',
    background: active ? '#eff6ff' : 'white',
    transition: 'border 0.2s ease, box-shadow 0.2s ease',
  }),
  templateTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: '12px',
    borderRadius: '999px',
    background: '#e0f2fe',
    color: '#0369a1',
    marginTop: '8px',
  },
  controlsRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: '16px',
  },
  select: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    minWidth: '200px',
  },
  button: (variant = 'primary', fullWidth = false) => ({
    padding: '12px 18px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 600,
    background: variant === 'primary' ? '#2563eb' : variant === 'danger' ? '#ef4444' : '#e5e7eb',
    color: variant === 'primary' || variant === 'danger' ? '#fff' : '#111827',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: fullWidth ? '100%' : 'auto',
  }),
  transcriptBox: {
    width: '100%',
    minHeight: '220px',
    borderRadius: '10px',
    border: '1px solid #d1d5db',
    padding: '16px',
    fontFamily: 'monospace',
    background: '#f9fafb',
    whiteSpace: 'pre-wrap',
    overflowY: 'auto',
  },
  diarizationList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  diarizationItem: {
    padding: '10px 0',
    borderBottom: '1px solid #e5e7eb',
  },
  statusPill: (status) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    background: status === 'completed' ? '#dcfce7' : status === 'failed' ? '#fee2e2' : '#e0e7ff',
    color: status === 'completed' ? '#166534' : status === 'failed' ? '#b91c1c' : '#1d4ed8',
    fontWeight: 600,
    fontSize: '13px',
  }),
  sessionList: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  sessionRow: {
    borderBottom: '1px solid #e5e7eb',
  },
  toggleButton: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: 'white',
    color: '#2563eb',
    fontWeight: 600,
    fontSize: '13px',
    cursor: 'pointer',
  },
};

const MeetingsPage = () => {
  const [config, setConfig] = useState({ default_expiry_hours: 48, llm: {} });
  const [templates, setTemplates] = useState([]);
  const settingsRef = useRef(loadSettings());
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(
    settingsRef.current.lastMeetingTemplate || ''
  );
  const [provider, setProvider] = useState(settingsRef.current.lastMeetingProvider || 'azure');
  const [language, setLanguage] = useState(settingsRef.current.lastMeetingLanguage || 'en-GB');
  const [expectedSpeakers, setExpectedSpeakers] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [diarizedSegments, setDiarizedSegments] = useState([]);
  const [processedMarkdown, setProcessedMarkdown] = useState('');
  const [processedText, setProcessedText] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [recentSessions, setRecentSessions] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [textUploadTitle, setTextUploadTitle] = useState('');
  const [textUploadContent, setTextUploadContent] = useState('');
  const [textUploadFormat, setTextUploadFormat] = useState('text');
  const [isTextUploading, setIsTextUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isLiveTranscriptVisible, setIsLiveTranscriptVisible] = useState(false);
  const [isRawTranscriptVisible, setIsRawTranscriptVisible] = useState(false);

  const navigate = useNavigate();
  const { loading } = useAuth();
  const wsRef = useRef(null);
  const audioStreamRef = useRef(null);
  const pcmStreamerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const pollTimerRef = useRef(null);
  const lastStatusRef = useRef('');
  const pendingFramesRef = useRef([]);
  const speakerColorsRef = useRef({});
  const chunkSeqRef = useRef(0);
  const chunkSaveQueueRef = useRef(Promise.resolve());
  const liveSessionIdRef = useRef(null);

  const speakerOptions = useMemo(
    () => [
      { value: 0, label: 'Auto-detect Speakers' },
      { value: 1, label: '1 Speaker' },
      { value: 2, label: '2 Speakers' },
      { value: 3, label: '3 Speakers' },
      { value: 4, label: '4 Speakers' },
      { value: 5, label: '5 Speakers' },
      { value: 6, label: '6 Speakers' },
      { value: 7, label: '7 Speakers' },
      { value: 8, label: '8 Speakers' },
      { value: 9, label: '9 Speakers' },
      { value: 10, label: '10+ Speakers' },
    ],
    []
  );

  const enqueueChunkSave = (sessionId, blob, sequence) => {
    chunkSaveQueueRef.current = chunkSaveQueueRef.current
      .catch(() => null)
      .then(() => saveAudioChunk(sessionId, blob, sequence))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to persist audio chunk', error);
      });
  };

  const providerDisplayNames = useMemo(
    () => ({
      azure: 'Azure Speech Service',
      speechmatics: 'Speechmatics',
      sherpa: 'Sherpa ONNX (local)',
    }),
    []
  );

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.key === selectedTemplateKey) || null,
    [templates, selectedTemplateKey]
  );

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const cleanupStreaming = () => {
    if (pcmStreamerRef.current) {
      try {
        pcmStreamerRef.current.close();
      } catch (err) {
        // ignore
      }
      pcmStreamerRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (err) {
        // ignore
      }
      mediaRecorderRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }
      } catch (err) {
        // ignore
      }
      wsRef.current = null;
    }
    pendingFramesRef.current = [];
    chunkSeqRef.current = 0;
    liveSessionIdRef.current = null;
    setIsStreaming(false);
  };

  useEffect(() => {
    async function loadInitial() {
      try {
        const [cfg, tpl, sessions] = await Promise.all([
          fetchMeetingConfig(),
          fetchMeetingTemplates(),
          listMeetingSessions(),
        ]);
        setConfig(cfg);
        setTemplates(tpl);
        setRecentSessions(sessions);
        if (tpl.length) {
          const preferredTemplate = settingsRef.current.lastMeetingTemplate;
          if (preferredTemplate && tpl.find((t) => t.key === preferredTemplate)) {
            setSelectedTemplateKey(preferredTemplate);
          } else if (!selectedTemplateKey) {
            setSelectedTemplateKey(tpl[0].key);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load meeting data', error);
        toast.error('Unable to load meeting configuration');
      } finally {
        setIsLoading(false);
      }
    }

    loadInitial();

    return () => {
      stopPolling();
      cleanupStreaming();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    async function checkRecovery() {
      try {
        const interrupted = await findInterruptedSessions();
        if (!active || !interrupted.length) {
          return;
        }
        const doRecover = window.confirm(
          `Found ${interrupted.length} unsaved recording(s) from a previous crash. Recover now?`
        );
        if (!doRecover) {
          return;
        }
        const session = interrupted[0];
        const blob = await recoverRecordingBlob(session.id);
        setUploadFile(new File([blob], `recovered-${session.id}.webm`, { type: 'audio/webm' }));
        toast.success("Recording recovered! Click 'Process recording' to save it.");
        await finishLocalSession(session.id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to recover interrupted recordings', error);
      }
    }

    checkRecovery();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (templates.length && !selectedTemplateKey) {
      setSelectedTemplateKey(templates[0].key);
    }
  }, [templates, selectedTemplateKey]);

  useEffect(() => {
    persistSettings({
      ...settingsRef.current,
      lastMeetingProvider: provider,
      lastMeetingTemplate: selectedTemplateKey,
      lastMeetingLanguage: language,
    });
  }, [provider, selectedTemplateKey, language]);

  useEffect(() => {
    if (config.providers?.length && !config.providers.includes(provider)) {
      setProvider(config.providers[0]);
    }
  }, [config.providers, provider]);

  const updateDetailState = (detail) => {
    if (!detail) return;
    setSessionDetail(detail);
    setJobStatus(detail.status);
    lastStatusRef.current = detail.status;
    if (detail.raw_transcript) {
      setLiveTranscript(detail.raw_transcript);
    }
    if (Array.isArray(detail.diarized_transcript)) {
      setDiarizedSegments(detail.diarized_transcript);
    }
    if (detail.llm_output_markdown) {
      setProcessedMarkdown(detail.llm_output_markdown);
    }
    if (detail.llm_output_text) {
      setProcessedText(detail.llm_output_text);
    }
  };

  const pollSession = (sessionId) => {
    if (!sessionId) return;
    stopPolling();

    const fetchDetail = async () => {
      try {
        const detail = await fetchMeetingSession(sessionId);
        updateDetailState(detail);
        if (detail.status === 'completed' || detail.status === 'failed') {
          stopPolling();
          const sessions = await listMeetingSessions();
          setRecentSessions(sessions);
          if (detail.status === 'completed') {
            toast.success('Meeting transcript ready');
          } else {
            toast.error('Meeting processing failed');
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to poll meeting session', error);
      }
    };

    fetchDetail();
    pollTimerRef.current = setInterval(fetchDetail, 4000);
  };

  const startLiveMeeting = async () => {
    if (isStreaming) return;
    if (!selectedTemplateKey) {
      toast.error('Select a meeting template to begin');
      return;
    }

    try {
      const payload = {
        title: `Live meeting ${new Date().toLocaleString()}`,
        template_key: selectedTemplateKey,
        meeting_type: selectedTemplate?.category || 'Live meeting',
        mode: 'stream',
        diarization_enabled: true,
        expected_speakers: expectedSpeakers || null,
        provider,
        language_code: language,
      };
      const session = await createMeetingSession(payload);
      setSessionDetail(null);
      setLiveTranscript('');
      setDiarizedSegments([]);
      setProcessedMarkdown('');
      setProcessedText('');
      setJobStatus(session.status);
      lastStatusRef.current = session.status;
      setRecentSessions((prev) => [session, ...prev.filter((item) => item.id !== session.id)]);
      liveSessionIdRef.current = session.id;
      chunkSeqRef.current = 0;
      chunkSaveQueueRef.current = Promise.resolve();
      try {
        await startLocalSession(session.id);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to initialize local recording store', error);
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = mediaStream;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/stt/transcribe/realtime?provider=${provider}&meeting_session_id=${session.id}`;
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;
      socket.binaryType = 'arraybuffer';
      pendingFramesRef.current = [];

      socket.onopen = () => {
        setIsStreaming(true);
        try {
          socket.send(
            JSON.stringify({
              type: 'config',
              autoExpand: true,
              overrides: {},
            })
          );
        } catch (err) {
          // ignore
        }
        const buffered = pendingFramesRef.current;
        if (buffered.length) {
          buffered.forEach((frame) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(frame);
            }
          });
          pendingFramesRef.current = [];
        }
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'partial') {
            // Ignore partial updates for now but could display ephemeral text
          } else if (data.type === 'final') {
            setLiveTranscript((prev) => (prev ? `${prev}\n${data.text}` : data.text));
          } else if (data.type === 'complete') {
            setLiveTranscript(data.text || '');
            pollSession(session.id);
          } else if (data.type === 'error') {
            toast.error(data.message || 'Streaming error');
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Invalid websocket payload', error);
        }
      };

      socket.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket error', error);
        toast.error('WebSocket error during meeting stream');
      };

      socket.onclose = () => {
        cleanupStreaming();
      };

      pcmStreamerRef.current = await createPcmStreamer(mediaStream, {
        onFrame: (bytes) => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(bytes);
          } else {
            pendingFramesRef.current.push(bytes);
          }
        },
      });
      const backupRecorder = new MediaRecorder(mediaStream);
      backupRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          const sequence = chunkSeqRef.current++;
          enqueueChunkSave(session.id, event.data, sequence);
        }
      };
      backupRecorder.start(5000);
      mediaRecorderRef.current = backupRecorder;
    } catch (error) {
      cleanupStreaming();
      // eslint-disable-next-line no-console
      console.error('Failed to start live meeting', error);
      toast.error('Unable to start live meeting');
    }
  };

  const stopLiveMeeting = async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send('stop');
      } catch (error) {
        // ignore
      }
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        // ignore
      }
    }
    try {
      await chunkSaveQueueRef.current;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to flush audio chunk queue', error);
    }
    if (liveSessionIdRef.current) {
      try {
        await finishLocalSession(liveSessionIdRef.current);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to finalize local recording', error);
      } finally {
        liveSessionIdRef.current = null;
      }
    }
    cleanupStreaming();
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Select an audio file to upload');
      return;
    }
    if (!selectedTemplateKey) {
      toast.error('Select a meeting template');
      return;
    }

    try {
      setIsUploading(true);
      const payload = {
        title: uploadFile.name,
        template_key: selectedTemplateKey,
        meeting_type: selectedTemplate?.category || 'Uploaded meeting',
        mode: 'upload',
        diarization_enabled: true,
        expected_speakers: expectedSpeakers || null,
        provider,
        language_code: language,
      };
      const session = await createMeetingSession(payload);
      setSessionDetail(null);
      setLiveTranscript('');
      setDiarizedSegments([]);
      setProcessedMarkdown('');
      setProcessedText('');
      setJobStatus(session.status);
      lastStatusRef.current = session.status;
      setRecentSessions((prev) => [session, ...prev.filter((item) => item.id !== session.id)]);

      setUploadProgress(0);
      await uploadMeetingAudio(session.id, uploadFile, (percent) => setUploadProgress(percent));
      toast.success('Audio uploaded. Processing has started.');
      pollSession(session.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Upload failed', error);
      toast.error('Failed to upload audio for processing');
    } finally {
      setUploadProgress(0);
      setIsUploading(false);
    }
  };

  const handleTextUpload = async () => {
    if (!textUploadTitle.trim()) {
      toast.error('Enter a title for this transcript');
      return;
    }
    if (!textUploadContent.trim()) {
      toast.error('Paste transcript content to continue');
      return;
    }

    try {
      setIsTextUploading(true);
      const session = await uploadTextSession({
        title: textUploadTitle.trim(),
        content: textUploadContent,
        format: textUploadFormat,
        template_key: selectedTemplateKey || null,
      });

      toast.success('Transcript queued for summarization');
      setRecentSessions((prev) => [session, ...prev.filter((item) => item.id !== session.id)]);
      navigate(`/meetings/${session.id}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to upload transcript', error);
      toast.error('Unable to upload transcript for processing');
    } finally {
      setIsTextUploading(false);
    }
  };

  const handleSelectSession = async (sessionId) => {
    try {
      const detail = await fetchMeetingSession(sessionId);
      updateDetailState(detail);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load meeting session', error);
      toast.error('Unable to load session details');
    }
  };

  const copyText = async (text, label) => {
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    if (!navigator.clipboard) {
      toast.error('Clipboard not available');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (error) {
      toast.error('Unable to copy text');
    }
  };

  const getSpeakerColor = (speakerId) => {
    const palette = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#14b8a6', '#f97316', '#8b5cf6', '#0ea5e9', '#22c55e'];
    const key = speakerId || 'Speaker';
    if (!speakerColorsRef.current[key]) {
      const assigned = Object.keys(speakerColorsRef.current).length;
      speakerColorsRef.current[key] = palette[assigned % palette.length];
    }
    return speakerColorsRef.current[key];
  };

  const renderDiarization = () => {
    if (!diarizedSegments.length) {
      return <p style={{ color: '#6b7280' }}>Diarized transcript will appear once processing completes.</p>;
    }
    return (
      <ul style={styles.diarizationList}>
        {diarizedSegments.map((segment, index) => (
          <li key={`${segment.speaker}-${index}`} style={styles.diarizationItem}>
            <button
              type="button"
              onClick={() => renameSpeaker(segment.speaker)}
              style={{
                color: getSpeakerColor(segment.speaker),
                fontWeight: 700,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              {segment.speaker || `Speaker ${index + 1}`}:
            </button>
            <span style={{ marginLeft: '8px', color: '#374151' }}>{segment.text}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renameSpeaker = async (speaker) => {
    if (!sessionDetail?.id || !speaker) return;
    const newName = window.prompt(`Rename ${speaker} to:`, speaker);
    if (!newName || newName === speaker) return;
    try {
      const updated = await renameMeetingSpeakers(
        sessionDetail.id,
        { [speaker]: newName },
        true
      );
      updateDetailState(updated);
      toast.success('Speaker name updated');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to rename speaker', error);
      toast.error('Unable to rename speaker');
    }
  };

  const renderStatus = () => {
    if (!jobStatus) return null;
    return (
      <div>
        <div style={styles.statusPill(jobStatus)}>
          <span>{jobStatus.toUpperCase()}</span>
        </div>
        {jobStatus === 'failed' && sessionDetail?.last_error && (
          <p style={{ color: '#b91c1c', marginTop: '8px' }}>{sessionDetail.last_error}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.section}>
          <h1 style={styles.title}>Meeting transcription workspace</h1>
          <p style={{ color: '#4b5563' }}>Checking permissions before loading the workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.section}>
        <h1 style={styles.title}>Meeting transcription workspace</h1>
        <p style={{ color: '#4b5563', marginBottom: '16px' }}>
          Capture meetings in real time or process existing recordings. Diarization and templated LLM summaries are handled
          automatically, and audio downloads follow the configured retention window. You will only
          see sessions that you created or ones that have been explicitly shared with you.
        </p>
        <div style={styles.controlsRow}>
          <label style={{ fontWeight: 600, color: '#1f2937' }}>Participants:</label>
          <select
            value={expectedSpeakers}
            onChange={(e) => setExpectedSpeakers(Number(e.target.value))}
            style={styles.select}
          >
            {speakerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label style={{ fontWeight: 600, color: '#1f2937' }}>Provider</label>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} style={styles.select}>
            {(config.providers || ['azure']).map((value) => (
              <option key={value} value={value}>
                {providerDisplayNames[value] || value}
              </option>
            ))}
          </select>
          <label style={{ fontWeight: 600, color: '#1f2937' }}>Language</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} style={styles.select}>
            {(config.languages || []).map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            style={styles.button('secondary')}
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced settings'}
          </button>
        </div>
        {isLoading ? (
          <p>Loading meeting configuration…</p>
        ) : (
          <>
            <h2 style={styles.subtitle}>Templates</h2>
            <div style={styles.templateGrid}>
              {templates.map((template) => (
                <div
                  key={template.id}
                  style={styles.templateCard(template.key === selectedTemplateKey)}
                  onClick={() => setSelectedTemplateKey(template.key)}
                >
                  <div style={styles.templateTitle}>{template.name}</div>
                  {template.category && <div style={styles.badge}>{template.category}</div>}
                  <p style={{ color: '#4b5563', fontSize: '14px', marginTop: '10px' }}>{template.description}</p>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '20px' }}>
              <div style={styles.controlsRow}>
                <span style={{ color: '#4b5563' }}>
                  Minutes are written by <strong>{config.llm?.model || 'the configured model'}</strong>
                  {' '}(set by an admin)
                </span>
                {renderStatus()}
              </div>
            </div>
          </>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.subtitle}>Live meeting capture</h2>
        <p style={{ color: '#4b5563', marginBottom: '16px' }}>
          Start a live meeting to stream audio directly to the transcription service. Once the stream ends the meeting is
          diarized and summarised automatically.
        </p>
        <div style={styles.controlsRow}>
          <button
            type="button"
            style={styles.button('primary')}
            onClick={startLiveMeeting}
            disabled={isStreaming || isLoading}
          >
            {isStreaming ? 'Recording…' : 'Start live meeting'}
          </button>
          <button
            type="button"
            style={styles.button('danger')}
            onClick={stopLiveMeeting}
            disabled={!isStreaming}
          >
            Stop meeting
          </button>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h3 style={styles.subtitle}>Live transcript</h3>
            <button
              type="button"
              style={{
                ...styles.toggleButton,
                background: isLiveTranscriptVisible ? '#eef2ff' : '#fff',
              }}
              onClick={() => setIsLiveTranscriptVisible((prev) => !prev)}
            >
              {isLiveTranscriptVisible ? 'Hide transcript' : 'Show transcript'}
            </button>
          </div>
          {isLiveTranscriptVisible ? (
            <div style={styles.transcriptBox}>{liveTranscript || 'Begin streaming to populate the transcript.'}</div>
          ) : (
            <p style={{ color: '#6b7280' }}>Live transcript is hidden to keep the layout compact. Click “Show transcript” to expand it.</p>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.subtitle}>Upload a recording</h2>
        <p style={{ color: '#4b5563', marginBottom: '16px' }}>
          Upload audio from an existing meeting to run the diarization and LLM summary pipeline.
        </p>
        <div style={styles.controlsRow}>
          <input
            type="file"
            accept="audio/*"
            onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
            style={{ flex: 1, minWidth: '240px' }}
          />
          <button
            type="button"
            style={styles.button('secondary')}
            onClick={handleUpload}
            disabled={!uploadFile || isUploading || isLoading}
          >
            {isUploading ? 'Uploading…' : 'Process recording'}
          </button>
          {isUploading && (
            <div style={{ minWidth: '160px', color: '#2563eb', fontWeight: 600 }}>
              Uploading… {uploadProgress}%
            </div>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.subtitle}>Upload a transcript</h2>
        <p style={{ color: '#4b5563', marginBottom: '16px' }}>
          Paste meeting notes or JSON diarized logs to generate a summary without processing audio.
        </p>
        <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: '1fr', marginBottom: '12px' }}>
          <input
            type="text"
            placeholder="Transcript title"
            value={textUploadTitle}
            onChange={(event) => setTextUploadTitle(event.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
          />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontWeight: 600, color: '#374151' }}>Format:</label>
            <select
              value={textUploadFormat}
              onChange={(event) => setTextUploadFormat(event.target.value)}
              style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', minWidth: '160px' }}
            >
              <option value="text">Plain text</option>
              <option value="json">JSON diarized list</option>
            </select>
          </div>
          <textarea
            placeholder="Paste transcript content or JSON array of {speaker, text} entries"
            value={textUploadContent}
            onChange={(event) => setTextUploadContent(event.target.value)}
            style={{
              minHeight: '180px',
              borderRadius: '10px',
              border: '1px solid #d1d5db',
              padding: '12px',
              fontFamily: 'monospace',
              background: '#f9fafb',
            }}
          />
          <div>
            <button
              type="button"
              style={styles.button('primary')}
              onClick={handleTextUpload}
              disabled={isTextUploading || isLoading}
            >
              {isTextUploading ? 'Queuing…' : 'Summarize transcript'}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.subtitle}>Processed outputs</h2>
        {sessionDetail ? (
          <>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => copyText(liveTranscript, 'Transcript')}
                disabled={!liveTranscript}
              >
                Copy transcript
              </button>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => copyText(processedMarkdown, 'Summary (Markdown)')}
                disabled={!processedMarkdown}
              >
                Copy summary (Markdown)
              </button>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => copyText(processedText, 'Summary (Text)')}
                disabled={!processedText}
              >
                Copy summary (Text)
              </button>
              {sessionDetail?.audio_download_available && (
                <button
                  type="button"
                  style={styles.button('secondary')}
                  onClick={() => downloadMeetingArtifact(sessionDetail.id, 'audio')}
                >
                  Download audio (FLAC)
                </button>
              )}
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => downloadMeetingArtifact(sessionDetail.id, 'raw')}
                disabled={!sessionDetail.raw_transcript}
              >
                Download transcript
              </button>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => downloadMeetingArtifact(sessionDetail.id, 'transcript_json')}
                disabled={!sessionDetail.diarized_transcript?.length}
              >
                Download transcript (JSON)
              </button>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => downloadMeetingArtifact(sessionDetail.id, 'transcript_text')}
                disabled={!sessionDetail.raw_transcript}
              >
                Download transcript (Text)
              </button>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => downloadMeetingArtifact(sessionDetail.id, 'processed_markdown')}
                disabled={!processedMarkdown}
              >
                Download summary (.md)
              </button>
              <button
                type="button"
                style={styles.button('secondary')}
                onClick={() => downloadMeetingArtifact(sessionDetail.id, 'processed_text')}
                disabled={!processedText}
              >
                Download summary (.txt)
              </button>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <h3 style={styles.subtitle}>Raw transcript</h3>
                <button
                  type="button"
                  style={{
                    ...styles.toggleButton,
                    background: isRawTranscriptVisible ? '#eef2ff' : '#fff',
                  }}
                  onClick={() => setIsRawTranscriptVisible((prev) => !prev)}
                >
                  {isRawTranscriptVisible ? 'Hide transcript' : 'Show transcript'}
                </button>
              </div>
              {isRawTranscriptVisible ? (
                <div style={styles.transcriptBox}>{liveTranscript || 'Transcript not yet available.'}</div>
              ) : (
                <p style={{ color: '#6b7280' }}>
                  Transcript content is hidden by default because it can be very long. Click “Show transcript” to view the
                  raw text.
                </p>
              )}
            </div>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={styles.subtitle}>Diarized segments</h3>
              {renderDiarization()}
            </div>
            <div>
              <h3 style={styles.subtitle}>LLM summary</h3>
              <div style={styles.transcriptBox}>{processedMarkdown || 'Summary pending completion of processing.'}</div>
            </div>
          </>
        ) : (
          <p style={{ color: '#6b7280' }}>Start a meeting or upload audio to see processed outputs.</p>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.subtitle}>Recent sessions</h2>
        {recentSessions.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No meeting sessions yet.</p>
        ) : (
          <table style={styles.sessionList}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 8px' }}>Title</th>
                <th style={{ padding: '12px 8px' }}>Mode</th>
                <th style={{ padding: '12px 8px' }}>Status</th>
                <th style={{ padding: '12px 8px' }}>Created</th>
                <th style={{ padding: '12px 8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentSessions.map((session) => (
                <tr key={session.id} style={styles.sessionRow}>
                  <td style={{ padding: '10px 8px' }}>{session.title}</td>
                  <td style={{ padding: '10px 8px', textTransform: 'capitalize' }}>{session.mode}</td>
                  <td style={{ padding: '10px 8px' }}>
                    {session.status}
                    {session.status === 'failed' && session.last_error && (
                      <div style={{ color: '#b91c1c', fontSize: '12px' }}>{session.last_error}</div>
                    )}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {new Date(session.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <button
                      type="button"
                      style={styles.button('secondary')}
                      onClick={() => handleSelectSession(session.id)}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.button('secondary'), marginLeft: '8px' }}
                      onClick={() => navigate(`/meetings/${session.id}`)}
                    >
                      Workspace
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MeetingsPage;
